import { noChange, nothing, type Part } from "lit";
import {
  Directive,
  type DirectiveParameters,
  type DirectiveResult,
  directive,
  PartType,
} from "lit/directive.js";

import { assert } from "#core/utils/assert.ts";

type DeferredEmitter<E> = (emitter: (e: E) => void) => void;

type Validator = (
  field: HTMLElement,
  {
    onblur,
    onsubmit,
  }: {
    onblur: DeferredEmitter<FocusEvent>;
    onsubmit: DeferredEmitter<SubmitEvent>;
  },
) => void;

type Options = {
  reportValidity?: ElementInternals["reportValidity"];
  setCustomValidity?: (message: string) => void;
  getValidity?: () => ValidityState;
};

const isFormAssociatedCustomElement = (el: Element): el is HTMLElement =>
  el instanceof HTMLElement &&
  "formAssociated" in el.constructor &&
  el.constructor.formAssociated === true;

export const reformist: (
  validator?: Validator,
  options?: Options,
) => DirectiveResult = directive(
  class extends Directive {
    #installed = false;

    override render(_validator?: Validator, _options?: Options) {
      return nothing;
    }

    override update(
      part: Part,
      [validator, options]: DirectiveParameters<this>,
    ) {
      if (this.#installed) {
        return noChange;
      }

      assert(
        part.type === PartType.ELEMENT,
        "field directive is only usable on element tags",
      );

      assert(
        part.element instanceof HTMLInputElement ||
          part.element instanceof HTMLSelectElement ||
          part.element instanceof HTMLButtonElement ||
          part.element instanceof HTMLOutputElement ||
          part.element instanceof HTMLTextAreaElement ||
          isFormAssociatedCustomElement(part.element),
        "field directive is only usable on form associated elements",
      );

      const field = part.element as HTMLElement;

      let aborter = new AbortController();

      const diagnose = () => {
        // Remove all previously attached event listeners to prevent stale diagnostic emission.
        aborter.abort();
        aborter = new AbortController();

        // Cleanup custom errors
        (
          options?.setCustomValidity ??
          ((message) => {
            assert(
              "setCustomValidity" in field,
              "`setCustomValidity()` not found",
            );
            assert(
              typeof field.setCustomValidity === "function",
              "`setCustomValidity()` is not a function",
            );

            field.setCustomValidity(message);
          })
        )("");

        {
          let returned = false;

          validator?.(field, {
            onblur: (emitter) => {
              if (returned) {
                throw new Error(
                  "cannot setup deferred diagnostic emitter after event handler returned",
                );
              }

              field.addEventListener(
                "blur",
                (e: FocusEvent) => {
                  emitter(e);
                },
                { once: true, signal: aborter.signal },
              );
            },
            onsubmit: (emitter) => {
              if (returned) {
                throw new Error(
                  "cannot setup deferred diagnostic emitter after event handler returned",
                );
              }

              const form = part.element.closest("form");

              if (form === null) {
                throw new Error(
                  "unable to register submit event handler, no parent form found",
                );
              }

              form.addEventListener(
                "submit",
                (e: SubmitEvent) => {
                  emitter(e);
                },
                { capture: true, once: true, signal: aborter.signal },
              );
            },
          });

          returned = true;
        }

        {
          const { valueMissing } = (
            options?.getValidity ??
            (() => {
              assert("validity" in field, "`validity` not found");
              assert(
                field.validity instanceof ValidityState,
                "`validity` is not an instance of ValidityState",
              );

              return field.validity;
            })
          )();

          if (valueMissing) {
            // We only want to show the "field required" message on submit.
            // To prevent the message from showing after the content is erased we remove the attribute while we report the validity.
            field.removeAttribute("required");
          }

          (
            options?.reportValidity ??
            (() => {
              assert("reportValidity" in field, "`reportValidity()` not found");
              assert(
                typeof field.reportValidity === "function",
                "`reportValidity()` is not a function",
              );

              return field.reportValidity();
            })
          )();

          if (valueMissing) {
            field.setAttribute("required", "");
          }
        }
      };

      part.element.addEventListener("input", (e: Event): void => {
        if (e instanceof InputEvent && e.isComposing) {
          return;
        }

        diagnose();
      });

      this.#installed = true;

      diagnose();

      return noChange;
    }
  },
);
